import React from 'react'
import {useAsync} from 'react-async-hook'

import Feedback from 'airship/patterns/feedback'
import LoadingSpinner from '../LoadingSpinner'
import {Features} from 'airship/clients/feature-loader/types'
import {resolveFeatures} from 'airship/clients/feature-loader'

type Props<T extends Features> = {
  /** Array of features to resolve, use `as const` to improve type-checking */
  resolve: Readonly<T[]>
  /** Optional override for loading indicator */
  loadingDisplay?: JSX.Element
  /** Optional override for error alert */
  errorDisplay?: JSX.Element
  /** Render prop for resolved features */
  render: (features: Record<T, boolean>) => JSX.Element
}

function FeatureLoader<T extends Features>({
  resolve,
  errorDisplay,
  loadingDisplay,
  render,
}: Props<T>): JSX.Element {
  const {error, loading, result} = useAsync(resolveFeatures, [resolve])
  if (error) {
    return (
      errorDisplay ?? (
        <div data-testid="error-feedback">
          <Feedback type="error">An error occurred.</Feedback>
        </div>
      )
    )
  }
  if (loading || !result) {
    return (
      loadingDisplay ?? (
        <div data-testid="loading-indicator">
          <LoadingSpinner />
        </div>
      )
    )
  }

  return render(result)
}

export default FeatureLoader
